import { useMemo, type ReactElement } from 'react';
import type { LoadDay } from '@/lib/load';

/**
 * Vista mensual de la carga (agregación en cliente).
 *
 * Decisión: en lugar de añadir un endpoint backend nuevo `/api/load/monthly`,
 * reutilizamos `useLoadTimeline()` ya cargado y agrupamos los `LoadDay` por
 * (año, mes). Para cada mes mostramos:
 *   - TSS_mes = suma de `tss` en el mes (proxy de carga total).
 *   - CTL final = `ctl` del último día del mes que tenga dato.
 *   - ATL final = `atl` del último día del mes que tenga dato.
 *
 * Render: barra horizontal por mes (proporcional al TSS máximo) con
 * etiquetas CTL/ATL al final. Mantiene el "look & feel" de los charts
 * existentes (Tailwind + barras teal) sin librerías.
 */
export function MonthlyView({ days }: { days: LoadDay[] }): ReactElement {
  const months = useMemo(() => groupByMonth(days), [days]);

  if (months.length === 0) {
    return (
      <p
        role="status"
        className="rounded-md border border-dashed border-slate-300 px-3 py-6 text-center text-sm text-slate-600"
        data-testid="monthly-empty"
      >
        Sin datos para agrupar por mes.
      </p>
    );
  }

  const maxTss = Math.max(1, ...months.map((m) => m.tssTotal));

  return (
    <ul aria-label="Carga mensual" className="space-y-3" data-testid="monthly-view">
      {months.map((m) => {
        const pct = (m.tssTotal / maxTss) * 100;
        return (
          <li key={m.key} className="flex flex-wrap items-center gap-3">
            <span className="w-24 shrink-0 text-xs font-medium text-slate-700">
              {m.label}
            </span>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={Math.round(maxTss)}
              aria-valuenow={Math.round(m.tssTotal)}
              aria-label={`${Math.round(m.tssTotal)} TSS en ${m.label}`}
              className="h-3 flex-1 overflow-hidden rounded bg-slate-100"
            >
              <div className="h-full bg-teal-600" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-16 shrink-0 text-right text-xs text-slate-700">
              {Math.round(m.tssTotal)} TSS
            </span>
            <span className="w-32 shrink-0 text-right text-xs text-slate-500">
              CTL {m.ctlEnd.toFixed(0)} · ATL {m.atlEnd.toFixed(0)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Agrupación
// ---------------------------------------------------------------------------

type MonthGroup = {
  key: string; // 'YYYY-MM'
  label: string; // 'may 2026'
  tssTotal: number;
  ctlEnd: number;
  atlEnd: number;
  lastDayISO: string;
};

/**
 * Agrupa `days` por (año, mes) usando UTC (igual que el backend en
 * `daily_load.date_iso`). Mantiene el orden cronológico ascendente.
 */
export function groupByMonth(days: LoadDay[]): MonthGroup[] {
  if (days.length === 0) return [];
  const buckets = new Map<string, MonthGroup>();
  for (const d of days) {
    const date = new Date(d.dateISO);
    if (!Number.isFinite(date.getTime())) continue;
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth(); // 0-11
    const key = `${year}-${String(month + 1).padStart(2, '0')}`;
    const existing = buckets.get(key);
    if (!existing) {
      buckets.set(key, {
        key,
        label: formatMonthLabel(year, month),
        tssTotal: d.tss,
        ctlEnd: d.ctl,
        atlEnd: d.atl,
        lastDayISO: d.dateISO,
      });
    } else {
      existing.tssTotal += d.tss;
      // Tomamos los valores del día más reciente dentro del mes.
      if (d.dateISO >= existing.lastDayISO) {
        existing.ctlEnd = d.ctl;
        existing.atlEnd = d.atl;
        existing.lastDayISO = d.dateISO;
      }
    }
  }
  return Array.from(buckets.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
}

function formatMonthLabel(year: number, month: number): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month, 15)));
  } catch {
    return `${year}-${String(month + 1).padStart(2, '0')}`;
  }
}
